import "./UnsupervisedResults.css";
import React, { useEffect, useRef, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Plot from "react-plotly.js";
import Papa from "papaparse";
import { apiRequest, logout, getTaskStatus, getAuthToken } from "../utils/api";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const CLUSTER_COLORS = [
  '#1e3c72', '#e07b00', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#17becf', '#bcbd22', '#aec7e8'
];

const PLOT_CONFIG = { responsive: true, displayModeBar: true };
const UNSUPERVISED_RESULTS_CACHE_KEY = "cachedUnsupervisedResults";

function cacheUnsupervisedResults(results) {
  try {
    localStorage.setItem(UNSUPERVISED_RESULTS_CACHE_KEY, JSON.stringify(results));
  } catch (error) {
    console.warn("Unable to cache unsupervised results:", error);
    localStorage.removeItem(UNSUPERVISED_RESULTS_CACHE_KEY);
  }
}

/**
 * StatefulPlot — used for static charts (dendrogram, elbow plot) that have no
 * user-selectable axes. Stable useMemo refs prevent the clock-tick re-renders
 * from triggering Plotly.react() unnecessarily.
 */
function StatefulPlot({ data, layout, config, style }) {
  const stableLayout = useMemo(() => ({ ...layout }), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const stableData   = useMemo(() => data, [data]);             // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Plot data={stableData} layout={stableLayout} config={config} style={style} useResizeHandler />
  );
}

/**
 * InteractiveScatter2D — React-owned axis selectors that rebuild the Plotly
 * trace from raw clustered_data whenever X or Y is changed. No Plotly dropdown
 * magic — just plain React state driving a fresh chart render.
 */
function InteractiveScatter2D({ clusteredData }) {
  const features = useMemo(
    () => Object.keys(clusteredData[0]).filter(k => k !== 'Cluster'),
    [clusteredData]
  );
  const [xAxis, setXAxis] = useState(() => features[0] || '');
  const [yAxis, setYAxis] = useState(() => features[1] || features[0] || '');
  const [selectorOpen, setSelectorOpen] = useState(false);

  const clusters = useMemo(
    () => [...new Set(clusteredData.map(r => r.Cluster))].sort((a, b) => a - b),
    [clusteredData]
  );

  const traces = useMemo(() =>
    clusters.map((cluster, i) => {
      const rows = clusteredData.filter(r => r.Cluster === cluster);
      return {
        x: rows.map(r => r[xAxis]),
        y: rows.map(r => r[yAxis]),
        type: 'scatter',
        mode: 'markers',
        name: `Cluster ${cluster}`,
        marker: { size: 8, color: CLUSTER_COLORS[i % CLUSTER_COLORS.length], opacity: 0.8 }
      };
    }),
    [clusteredData, clusters, xAxis, yAxis]
  );

  const layout = useMemo(() => ({
    xaxis: {
      title: { text: xAxis, font: { size: 14, color: '#1e3c72', family: 'Arial, sans-serif' } },
      tickfont: { size: 11 },
    },
    yaxis: {
      title: { text: yAxis, font: { size: 14, color: '#1e3c72', family: 'Arial, sans-serif' } },
      tickfont: { size: 11 },
    },
    template: 'plotly_white',
    showlegend: true,
    legend: { title: { text: 'Cluster' } },
    margin: { l: 70, r: 40, t: 30, b: 70 },
    autosize: true,
  }), [xAxis, yAxis]);

  useEffect(() => {
    const id = setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
    return () => clearTimeout(id);
  }, [selectorOpen, xAxis, yAxis]);

  if (features.length < 1) return <p>Not enough features for scatter plot.</p>;

  return (
    <div className="interactive-chart-wrapper">
      <button className="axis-toggle-btn" onClick={() => setSelectorOpen(o => !o)}>
        Configure Axes <span className="axis-toggle-chevron">{selectorOpen ? '▲' : '▼'}</span>
      </button>
      {selectorOpen && (
        <div className="axis-selector-row">
          <div className="axis-selector-group">
            <span className="axis-selector-label">X Axis</span>
            <div className="axis-btn-group">
              {features.map(f => (
                <button key={f} className={`axis-btn ${xAxis === f ? 'active' : ''}`} onClick={() => setXAxis(f)}>{f}</button>
              ))}
            </div>
          </div>
          <div className="axis-selector-group">
            <span className="axis-selector-label">Y Axis</span>
            <div className="axis-btn-group">
              {features.map(f => (
                <button key={f} className={`axis-btn ${yAxis === f ? 'active' : ''}`} onClick={() => setYAxis(f)}>{f}</button>
              ))}
            </div>
          </div>
        </div>
      )}
      <Plot
        data={traces}
        layout={layout}
        config={PLOT_CONFIG}
        style={{ width: '100%', height: '450px' }}
        useResizeHandler
      />
    </div>
  );
}

/**
 * InteractiveScatter3D — same approach as 2D but with X, Y, Z selectors.
 */
function InteractiveScatter3D({ clusteredData }) {
  const features = useMemo(
    () => Object.keys(clusteredData[0]).filter(k => k !== 'Cluster'),
    [clusteredData]
  );
  const [xAxis, setXAxis] = useState(() => features[0] || '');
  const [yAxis, setYAxis] = useState(() => features[1] || features[0] || '');
  const [zAxis, setZAxis] = useState(() => features[2] || features[1] || features[0] || '');
  const [selectorOpen, setSelectorOpen] = useState(false);

  const clusters = useMemo(
    () => [...new Set(clusteredData.map(r => r.Cluster))].sort((a, b) => a - b),
    [clusteredData]
  );

  const traces = useMemo(() =>
    clusters.map((cluster, i) => {
      const rows = clusteredData.filter(r => r.Cluster === cluster);
      return {
        x: rows.map(r => r[xAxis]),
        y: rows.map(r => r[yAxis]),
        z: rows.map(r => r[zAxis]),
        type: 'scatter3d',
        mode: 'markers',
        name: `Cluster ${cluster}`,
        marker: { size: 5, color: CLUSTER_COLORS[i % CLUSTER_COLORS.length], opacity: 0.85 }
      };
    }),
    [clusteredData, clusters, xAxis, yAxis, zAxis]
  );

  const layout = useMemo(() => ({
    scene: {
      xaxis: { title: xAxis },
      yaxis: { title: yAxis },
      zaxis: { title: zAxis },
    },
    template: 'plotly_white',
    showlegend: true,
    legend: { title: { text: 'Cluster' } },
    margin: { l: 0, r: 0, t: 30, b: 0 },
    autosize: true,
  }), [xAxis, yAxis, zAxis]);

  useEffect(() => {
    const id = setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
    return () => clearTimeout(id);
  }, [selectorOpen, xAxis, yAxis, zAxis]);

  if (features.length < 2) return <p>Not enough features for 3D visualization.</p>;

  return (
    <div className="interactive-chart-wrapper">
      <button className="axis-toggle-btn" onClick={() => setSelectorOpen(o => !o)}>
        Configure Axes <span className="axis-toggle-chevron">{selectorOpen ? '▲' : '▼'}</span>
      </button>
      {selectorOpen && (
        <div className="axis-selector-row">
          <div className="axis-selector-group">
            <span className="axis-selector-label">X Axis</span>
            <div className="axis-btn-group">
              {features.map(f => (
                <button key={f} className={`axis-btn ${xAxis === f ? 'active' : ''}`} onClick={() => setXAxis(f)}>{f}</button>
              ))}
            </div>
          </div>
          <div className="axis-selector-group">
            <span className="axis-selector-label">Y Axis</span>
            <div className="axis-btn-group">
              {features.map(f => (
                <button key={f} className={`axis-btn ${yAxis === f ? 'active' : ''}`} onClick={() => setYAxis(f)}>{f}</button>
              ))}
            </div>
          </div>
          <div className="axis-selector-group">
            <span className="axis-selector-label">Z Axis</span>
            <div className="axis-btn-group">
              {features.map(f => (
                <button key={f} className={`axis-btn ${zAxis === f ? 'active' : ''}`} onClick={() => setZAxis(f)}>{f}</button>
              ))}
            </div>
          </div>
        </div>
      )}
      <Plot
        data={traces}
        layout={layout}
        config={PLOT_CONFIG}
        style={{ width: '100%', height: '550px' }}
        useResizeHandler
      />
    </div>
  );
}

/**
 * ClusterBarChart — shows count of data points per cluster. No axis selection needed.
 */
function ClusterBarChart({ clusteredData }) {
  const clusters = useMemo(
    () => [...new Set(clusteredData.map(r => r.Cluster))].sort((a, b) => a - b),
    [clusteredData]
  );
  const counts = useMemo(
    () => clusters.map(c => clusteredData.filter(r => r.Cluster === c).length),
    [clusteredData, clusters]
  );
  const traces = useMemo(() => [{
    x: clusters.map(c => `Cluster ${c}`),
    y: counts,
    type: 'bar',
    marker: { color: clusters.map((_, i) => CLUSTER_COLORS[i % CLUSTER_COLORS.length]) },
    text: counts.map(String),
    textposition: 'outside',
  }], [clusters, counts]);
  const layout = useMemo(() => ({
    xaxis: { title: 'Cluster' },
    yaxis: { title: 'Number of Data Points' },
    template: 'plotly_white',
    showlegend: false,
    margin: { l: 60, r: 40, t: 30, b: 60 },
    autosize: true,
  }), []);

  return (
    <Plot
      data={traces}
      layout={layout}
      config={PLOT_CONFIG}
      style={{ width: '100%', height: '400px' }}
      useResizeHandler
    />
  );
}

function UnsupervisedResults() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [time, setTime] = useState("");
  const [visiblePlots, setVisiblePlots] = useState({});
  const progressRef = useRef(null);
  const navigate = useNavigate();
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");

  const togglePlot = (algorithmIndex, plotType) => {
    const key = `${algorithmIndex}-${plotType}`;
    setVisiblePlots(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  useEffect(() => {
    const selectedAlgorithms = localStorage.getItem("selectedAlgorithms");
    if (!selectedAlgorithms || selectedAlgorithms.length === 0) {
      navigate("/unsupervised-algorithm-selection");
      return;
    }
    fetchResults(selectedAlgorithms);

    const interval = setInterval(() => {
      const currentTime = new Date();
      const hours = currentTime.getHours() % 12 || 12;
      const minutes = currentTime.getMinutes().toString().padStart(2, "0");
      const seconds = currentTime.getSeconds().toString().padStart(2, "0");
      const ampm = currentTime.getHours() >= 12 ? "PM" : "AM";
      setTime(`${hours}:${minutes}:${seconds} ${ampm}`);
    }, 1000);

    return () => {
      clearInterval(progressRef.current);
      clearInterval(interval);
    };
    // pollRef is in progressRef (reused for polling)
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const fetchResults = async (algorithms) => {
    setLoading(true);
    setProgress(0);

    // If user navigated back (not a fresh run), show cached results without re-clustering
    const triggerTraining = localStorage.getItem("triggerUnsupervisedTraining");
    if (triggerTraining !== "true") {
      const cachedResults = localStorage.getItem(UNSUPERVISED_RESULTS_CACHE_KEY);
      if (cachedResults) {
        try {
          setResults(JSON.parse(cachedResults));
          setLoading(false);
          return;
        } catch (e) { /* fall through to re-run */ }
      }
    }
    localStorage.removeItem("triggerUnsupervisedTraining");

    // Fake progress animation while waiting
    const fakeProgress = setInterval(() => {
      setProgress((prev) => (prev >= 85 ? 85 : prev + 5));
    }, 800);

    try {
      const algorithmsArray = JSON.parse(algorithms || "[]");

      // Dispatch job — immediate 202 response
      const dispatch = await apiRequest("/api/algorithms/unsupervised/run", {
        method: "POST",
        body: { selected_algorithms: algorithmsArray },
      });
      const { task_id: taskId } = dispatch;

      // Poll for completion
      let consecutiveErrors = 0;
      const MAX_ERRORS = 3;
      const pollTimeout = setTimeout(() => {
        if (progressRef.current) {
          clearInterval(progressRef.current);
          clearInterval(fakeProgress);
          setError("Clustering timed out. Please try again.");
          setLoading(false);
        }
      }, 10 * 60 * 1000);

      progressRef.current = setInterval(async () => {
        try {
          const statusRes = await getTaskStatus(taskId);
          consecutiveErrors = 0;
          if (statusRes.status === "completed") {
            clearInterval(progressRef.current);
            clearInterval(fakeProgress);
            clearTimeout(pollTimeout);
            setProgress(100);

            const data = statusRes.result || {};
            if (data.status === "success" && Array.isArray(data.results)) {
              setResults(data.results);
              setError(null);
              cacheUnsupervisedResults(data.results);
            } else {
              setResults([]);
              setError(data.message || "Unexpected response structure.");
            }
            setLoading(false);
          } else if (statusRes.status === "failed") {
            clearInterval(progressRef.current);
            clearInterval(fakeProgress);
            clearTimeout(pollTimeout);
            setError(statusRes.error_message || "Clustering job failed.");
            setLoading(false);
          }
        } catch (pollErr) {
          consecutiveErrors += 1;
          console.error(`Polling error (${consecutiveErrors}/${MAX_ERRORS}):`, pollErr);
          if (consecutiveErrors >= MAX_ERRORS) {
            clearInterval(progressRef.current);
            clearInterval(fakeProgress);
            clearTimeout(pollTimeout);
            setError("Lost connection to clustering job. Please try again.");
            setLoading(false);
          }
        }
      }, 4000);

    } catch (err) {
      clearInterval(fakeProgress);
      console.error("Error dispatching clustering job:", err);
      setError("Error starting clustering job.");
      setResults([]);
      setLoading(false);
    }
  };

  const filteredResults = results.filter(
    (result) =>
      result.algorithm &&
      result.algorithm !== "PCA (Principal Component Analysis)"
  );

  const handleGenerateReport = async () => {
    setReportLoading(true);
    setReportError("");
    try {
      const payload = { results: filteredResults };
      const response = await fetch(`${API_BASE_URL}/api/report/generate-unsupervised`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Report generation failed");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Clustering_Analysis_Report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Report error:", e);
      setReportError(e.message || "Failed to generate report");
    } finally {
      setReportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="unsupervised-results-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading clustering results... {progress}%</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="unsupervised-results-page">
        <div className="error-container">
          <h2>Error Loading Results</h2>
          <p>{error}</p>
          <button onClick={() => navigate("/unsupervised-algorithm-selection")} className="primary-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (filteredResults.length === 0) {
    return (
      <div className="unsupervised-results-page">
        <div className="error-container">
          <h2>No Results Available</h2>
          <p>No clustering results were generated. Please try again.</p>
          <button onClick={() => navigate("/unsupervised-algorithm-selection")} className="primary-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="unsupervised-results-page">
      {/* Top Navigation Bar */}
      <div className="top-nav">
        <div className="nav-content">
          <div className="nav-left-group">
            <div className="nav-logo" onClick={() => navigate("/")}>
              <img src="/HDASLogo.png" alt="HDAS" style={{ height: "80px", width: "auto" }} />
            </div>
            <button className="back-nav-button" onClick={() => navigate("/unsupervised-algorithm-selection")}>
              Back
            </button>
          </div>
          <div className="nav-right">
            <div className="digital-clock">{time}</div>
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        </div>
      </div>

      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <h1>Clustering Results</h1>
          <p className="header-subtitle">
            Discover patterns and groups in your healthcare data
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-content">
        {/* Info Box */}
        <div className="info-box">
          <h3>Understanding Clustering Metrics</h3>
          <p>
            <strong>Silhouette Score:</strong> Measures how similar objects are to their own cluster (range: -1 to 1, higher is better).
            <br />
            <strong>Calinski-Harabasz Score:</strong> Ratio of between-cluster to within-cluster variance (higher is better).
            <br />
            <strong>Davies-Bouldin Score:</strong> Average similarity ratio of clusters (lower is better).
          </p>
        </div>

        {/* Results Cards */}
        <div className="results-container">
          {filteredResults.map((result, index) => (
            <div className="result-card" key={index}>
              <div className="result-card-header">
                <h2>{result.algorithm}</h2>
              </div>

              <div className="result-card-content">
                {/* Metrics Table */}
                <div className="metrics-section">
                  <h3>Performance Metrics</h3>
                  <div className="table-wrapper">
    <table className="metrics-table">
      <thead>
        <tr>
          <th>Metric</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        {result.optimal_clusters && (
          <tr>
            <td>Optimal Clusters</td>
            <td>{result.optimal_clusters}</td>
          </tr>
        )}
                        {result.silhouette_score !== undefined && result.silhouette_score !== null && (
          <tr>
            <td>Silhouette Score</td>
            <td>{result.silhouette_score.toFixed(4)}</td>
          </tr>
        )}
                        {result.calinski_harabasz_score !== undefined && result.calinski_harabasz_score !== null && (
          <tr>
            <td>Calinski-Harabasz Score</td>
            <td>{result.calinski_harabasz_score.toFixed(2)}</td>
          </tr>
        )}
                        {result.davies_bouldin_score !== undefined && result.davies_bouldin_score !== null && (
          <tr>
            <td>Davies-Bouldin Score</td>
            <td>{result.davies_bouldin_score.toFixed(2)}</td>
          </tr>
        )}
      </tbody>
    </table>
        </div>
                </div>

                {/* Silhouette Description */}
                  {result.algorithm === "K-Means Clustering" &&
                    result.silhouette_score_description && (
                    <div className="description-box">
                      <h3>Silhouette Score Interpretation</h3>
                        <p>{result.silhouette_score_description}</p>
                      </div>
                    )}

                {/* Visualizations */}
                <div className="visualizations-section">
                  <div className="visualizations-header">
                    <h3>Visualizations</h3>
                    <div className="plot-buttons">
                      {result.plots?.dendrogram && (
                        <button
                          className={`plot-toggle-button ${visiblePlots[`${index}-dendrogram`] ? 'active' : ''}`}
                          onClick={() => togglePlot(index, 'dendrogram')}
                        >
                          Dendrogram
                        </button>
                      )}
                      {result.plots?.elbow_plot && (
                        <button
                          className={`plot-toggle-button ${visiblePlots[`${index}-elbow_plot`] ? 'active' : ''}`}
                          onClick={() => togglePlot(index, 'elbow_plot')}
                        >
                          Elbow Plot
                        </button>
                      )}
                      {result.clustered_data && result.clustered_data.length > 0 && (
                        <button
                          className={`plot-toggle-button ${visiblePlots[`${index}-scatter2d`] ? 'active' : ''}`}
                          onClick={() => togglePlot(index, 'scatter2d')}
                        >
                          2D Scatter Plot
                        </button>
                      )}
                      {result.clustered_data && result.clustered_data.length > 0 && (
                        <button
                          className={`plot-toggle-button ${visiblePlots[`${index}-scatter3d`] ? 'active' : ''}`}
                          onClick={() => togglePlot(index, 'scatter3d')}
                        >
                          3D Visualization
                        </button>
                      )}
                      {result.clustered_data && result.clustered_data.length > 0 && (
                        <button
                          className={`plot-toggle-button ${visiblePlots[`${index}-barchart`] ? 'active' : ''}`}
                          onClick={() => togglePlot(index, 'barchart')}
                        >
                          Bar Chart
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="plots-grid">
                    {result.plots?.dendrogram && visiblePlots[`${index}-dendrogram`] && (
                      <div className="plot-container full-width">
                        <h4>Dendrogram</h4>
                        <StatefulPlot
                          data={result.plots.dendrogram.data}
                          layout={result.plots.dendrogram.layout}
                          config={PLOT_CONFIG}
                          style={{ width: '100%', height: '500px' }}
                        />
                      </div>
                    )}

                    {result.plots?.elbow_plot && visiblePlots[`${index}-elbow_plot`] && (
                      <div className="plot-container half-width">
                        <h4>Elbow Plot</h4>
                        <StatefulPlot
                          data={result.plots.elbow_plot.data}
                          layout={result.plots.elbow_plot.layout}
                          config={PLOT_CONFIG}
                          style={{ width: '100%', height: '400px' }}
                        />
                      </div>
                    )}

                    {result.clustered_data && result.clustered_data.length > 0 && visiblePlots[`${index}-scatter2d`] && (
                      <div className="plot-container full-width">
                        <h4>2D Scatter Plot</h4>
                        <InteractiveScatter2D clusteredData={result.clustered_data} />
                      </div>
                    )}

                    {result.clustered_data && result.clustered_data.length > 0 && visiblePlots[`${index}-scatter3d`] && (
                      <div className="plot-container full-width">
                        <h4>3D Visualization</h4>
                        <InteractiveScatter3D clusteredData={result.clustered_data} />
                      </div>
                    )}
                    {result.clustered_data && result.clustered_data.length > 0 && visiblePlots[`${index}-barchart`] && (
                      <div className="plot-container half-width">
                        <h4>Cluster Size Distribution</h4>
                        <ClusterBarChart clusteredData={result.clustered_data} />
                      </div>
                    )}
                    {(() => {
                      const hasVisiblePlots = Object.keys(visiblePlots).some(key =>
                        key.startsWith(`${index}-`) && visiblePlots[key]
                      );
                      const hasAnyPlots = result.plots?.dendrogram ||
                        result.plots?.elbow_plot ||
                        (result.clustered_data && result.clustered_data.length > 0);
                      return hasAnyPlots && !hasVisiblePlots;
                    })() && (
                      <div className="no-plots-message">
                        <p>Click on a visualization button above to view the plots</p>
                      </div>
                    )}
                  </div>
                </div>

                </div>
              </div>
            ))}
        </div>

        {/* Action Buttons */}
        <div className="action-section">
          {filteredResults.filter(r => r.clustered_data).map((result, index) => (
            <button
              key={index}
              className="download-button"
              onClick={() => {
                const csv = Papa.unparse(result.clustered_data);
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `clustered_data_${result.algorithm.replace(/\s+/g, "_")}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Download {result.algorithm} Clustered Dataset
            </button>
          ))}
          <button
            onClick={handleGenerateReport}
            disabled={reportLoading}
            className="report-button"
          >
            {reportLoading ? (
              <>
                <span className="report-spinner"></span>
                Generating AI Report…
              </>
            ) : (
              <>
                📄 Generate AI Report
              </>
            )}
          </button>
          {reportError && (
            <p className="report-error">{reportError}</p>
          )}
          <button onClick={() => navigate("/dashboard")} className="primary-button">
            End Analysis
          </button>
        </div>
      </div>
    </div>
  );
}

export default UnsupervisedResults;
